import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Icon from "@material-ui/core/Icon";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import PageCardHeader from "components/Card/PageCardHeader.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DataTable from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  heroWrap: {
    position: "relative",
    width: "100%",
    height: "220px",
    borderRadius: "20px",
    overflow: "hidden",
    marginBottom: "30px",
    background: "linear-gradient(160deg, #0d1b6e 0%, #1a237e 60%, #1565c0 100%)",
    boxShadow: "0 20px 40px -15px rgba(26, 35, 126, 0.3)"
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)",
    zIndex: 1
  },
  heroContent: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    padding: "40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    color: "#fff"
  },
  heroTitle: { fontSize: "32px", fontWeight: 800, margin: 0, letterSpacing: "-0.5px" },
  heroSub: { fontSize: "15px", margin: "8px 0 0", opacity: 0.9, fontWeight: 400 },

  // KPI stat cards
  kpiRow: { display: "flex", gap: "16px", marginBottom: "32px", flexWrap: "wrap" },
  statCard: {
    flex: 1,
    minWidth: "200px",
    padding: "20px",
    borderRadius: "16px",
    background: "#fff",
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    transition: "transform 0.2s ease",
    border: "1px solid #f1f5f9",
    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 30px 0 rgba(0,0,0,0.08)" }
  },
  iconBox: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  statNumber: { fontSize: "20px", fontWeight: 800, color: "#1e293b", margin: 0 },
  statLabel: { fontSize: "12px", color: "#64748b", margin: "2px 0 0", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.4px" },

  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  courseName: { fontWeight: "700", color: "#1a237e" },
  codeBadge: { background: "#e8eaf6", color: "#1a237e", padding: "4px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: "700" },
  filterBar: {
    display: "flex", gap: "12px", alignItems: "center",
    padding: "16px 20px", background: "#f8f9fa", borderBottom: "1px solid #eee"
  },
  filterSelect: {
    padding: "8px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "13px",
    background: "#fff", outline: "none", transition: "border-color 0.2s",
    "&:focus": { borderColor: "#1a237e" }
  }
};

const BLANK = { code: "", name: "", creditUnits: "3", programmeId: "", semesterId: "", type: "core" };

class CourseList extends Component {
  state = {
    courses: [], programmes: [], semesters: [],
    loading: true, saving: false,
    open: false, editing: null,
    form: { ...BLANK }, error: null,
    filterProgramme: "", filterSemester: ""
  };

  componentDidMount() { this.fetchAll(); }

  fetchAll = async () => {
    try {
      const [cRes, pRes, sRes] = await Promise.all([
        api.get("/courses?limit=200"),
        api.get("/programmes?limit=100"),
        api.get("/semesters?limit=100")
      ]);
      const cd = cRes.data.data || cRes.data;
      const pd = pRes.data.data || pRes.data;
      const sd = sRes.data.data || sRes.data;
      this.setState({
        courses: Array.isArray(cd) ? cd : cd.data || [],
        programmes: Array.isArray(pd) ? pd : pd.data || [],
        semesters: Array.isArray(sd) ? sd : sd.data || [],
        loading: false
      });
    } catch (e) { this.setState({ loading: false }); }
  };

  openNew = () => this.setState({ open: true, editing: null, form: { ...BLANK }, error: null });
  openEdit = c => this.setState({ open: true, editing: c.id, form: { code: c.code, name: c.name, creditUnits: String(c.creditUnits), programmeId: c.programmeId || "", semesterId: c.semesterId || "", type: c.type || "core" }, error: null });
  handleClose = () => this.setState({ open: false });
  handleChange = field => e => { const v = e.target.value; this.setState(prev => ({ form: { ...prev.form, [field]: v } })); };

  handleSave = async () => {
    const { form, editing } = this.state;
    if (!form.code || !form.name || !form.programmeId) { this.setState({ error: "Code, name, and programme are required." }); return; }
    this.setState({ saving: true });
    try {
      if (editing) await api.put(`/courses/${editing}`, form);
      else await api.post("/courses", form);
      this.setState({ open: false, saving: false });
      this.fetchAll();
    } catch (e) { this.setState({ error: e.response?.data?.message || "Failed.", saving: false }); }
  };

  render() {
    const { classes } = this.props;
    const { courses, programmes, semesters, loading, open, form, saving, error, filterProgramme } = this.state;
    const filtered = filterProgramme ? courses.filter(c => c.programmeId === filterProgramme) : courses;

    const columns = [
      {
        id: "code",
        header: "Code",
        accessorFn: c => c.code,
        cell: info => <span className={classes.codeBadge}>{info.getValue()}</span>
      },
      {
        id: "name",
        header: "Course Name",
        accessorFn: c => c.name,
        cell: info => <span className={classes.courseName}>{info.getValue()}</span>
      },
      {
        id: "type",
        header: "Type",
        accessorFn: c => c.type,
        cell: info => (
          <span style={{
            color: info.row.original.type === "core" ? "#1a237e" : "#4a148c",
            background: info.row.original.type === "core" ? "#e8eaf6" : "#f3e5f5",
            padding: "2px 8px", borderRadius: "12px", fontSize: "10px", fontWeight: "800", textTransform: "uppercase"
          }}>{info.getValue()}</span>
        )
      },
      { id: "cu", header: "CU", accessorFn: c => c.creditUnits },
      { id: "programme", header: "Programme", accessorFn: c => c.programme?.name || "—" },
      { id: "semester", header: "Semester", accessorFn: c => c.semester?.name || "—" },
      {
        id: "actions",
        header: "",
        cell: info => (
          <Button size="sm" color="info" simple onClick={() => this.openEdit(info.row.original)} style={{ padding: "6px" }}>
            <Icon style={{ fontSize: "20px" }}>edit</Icon>
          </Button>
        )
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <div className={classes.heroWrap}>
            <div className={classes.heroOverlay} />
            <div className={classes.heroContent}>
              <h1 className={classes.heroTitle}>Academic Courses</h1>
              <p className={classes.heroSub}>
                Manage course modules, credit units, and curriculum mapping across academic programmes.
              </p>
              <div style={{ marginTop: "20px" }}>
                <Button color="white" onClick={this.openNew}>
                  <Icon style={{ marginRight: "8px" }}>add</Icon> New Course
                </Button>
              </div>
            </div>
          </div>
        </GridItem>

        <GridItem xs={12}>
          <div className={classes.kpiRow}>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#e3f2fd", color: "#1565c0" }}>
                <Icon>assignment</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{courses.length}</p>
                <p className={classes.statLabel}>Total Courses</p>
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#e8f5e9", color: "#2e7d32" }}>
                <Icon>star</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{courses.filter(c => c.type === "core").length}</p>
                <p className={classes.statLabel}>Core Modules</p>
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#f3e5f5", color: "#7b1fa2" }}>
                <Icon>bookmarks</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{courses.reduce((acc, c) => acc + (Number(c.creditUnits) || 0), 0)}</p>
                <p className={classes.statLabel}>Total CUs</p>
              </div>
            </div>
          </div>
        </GridItem>

        <GridItem xs={12}>
          <div className={classes.filterBar}>
            <span style={{ fontSize: "12px", fontWeight: "700", color: "#666", textTransform: "uppercase" }}>Filter By:</span>
            <select className={classes.filterSelect}
              value={filterProgramme} onChange={e => this.setState({ filterProgramme: e.target.value })}>
              <option value="">All Programmes</option>
              {programmes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <DataTable
            columns={columns}
            data={filtered}
            isLoading={loading}
            emptyState="No courses found."
          />
        </GridItem>

        <Dialog open={open} onClose={this.handleClose} maxWidth="sm" fullWidth>
          <DialogHeader title={this.state.editing ? "Edit Course" : "New Course"} />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <CustomInput labelText="Course Code *" id="ccode" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.code, onChange: this.handleChange("code") }} />
            <CustomInput labelText="Course Name *" id="cname" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.name, onChange: this.handleChange("name") }} />
            <CustomInput labelText="Credit Units" id="cunits" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.creditUnits, onChange: this.handleChange("creditUnits"), type: "number" }} />
            <FormControl className={classes.formControl}>
              <InputLabel>Programme *</InputLabel>
              <Select value={form.programmeId} onChange={this.handleChange("programmeId")}>
                <MenuItem value="">— Select —</MenuItem>
                {programmes.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <InputLabel>Semester</InputLabel>
              <Select value={form.semesterId} onChange={this.handleChange("semesterId")}>
                <MenuItem value="">— Any Semester —</MenuItem>
                {semesters.map(s => <MenuItem key={s.id} value={s.id}>{s.name} — {s.academicYear?.name || ""}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <InputLabel>Type</InputLabel>
              <Select value={form.type} onChange={this.handleChange("type")}>
                <MenuItem value="core">Core</MenuItem>
                <MenuItem value="elective">Elective</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={this.handleClose} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Save"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

CourseList.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(CourseList));
